import React, { useState } from "react";
import { Box, Typography, Slider, IconButton, styled } from "@mui/material";
import {
  PlayCircle,
  PauseCircle,
  VolumeUp,
  VolumeOff,
  VolumeDown,
} from "@mui/icons-material";

const CustomSlider = styled(Slider)(({ theme }) => ({
  color: theme.palette.primary.main,
  height: 4,
  "& .MuiSlider-thumb": {
    width: 12,
    height: 12,
    transition: "0.3s cubic-bezier(.47,1.64,.41,.8)",
    "&:before": {
      boxShadow: "0 2px 12px 0 rgba(0,0,0,0.4)",
    },
    "&:hover, &.Mui-focusVisible": {
      boxShadow: `0px 0px 0px 8px ${
        theme.palette.mode === "dark"
          ? "rgb(255 255 255 / 16%)"
          : "rgb(0 0 0 / 16%)"
      }`,
    },
  },
  "& .MuiSlider-rail": {
    opacity: 0.28,
  },
}));

function CustomAudioPlayer({ audioURL }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [objectUrl, setObjectUrl] = useState(null);
  const audioRef = React.useRef(null);

  // Determine the audio source based on input type
  const audioSource = React.useMemo(() => {
    if (!audioURL) return "";
    return objectUrl || audioURL;
  }, [audioURL, objectUrl]);

  // Create object URL when audioURL changes
  React.useEffect(() => {
    if (!audioURL) return;

    // If it's a File object, create object URL
    if (audioURL instanceof File || audioURL instanceof Blob) {
      const url = URL.createObjectURL(audioURL);
      setObjectUrl(url);

      // Cleanup function
      return () => {
        URL.revokeObjectURL(url);
        setObjectUrl(null);
      };
    }

    // If it's already a string URL, just use it directly
    return () => {};
  }, [audioURL]);

  const handlePlayPause = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleVolumeChange = (event, newValue) => {
    setVolume(newValue);
    audioRef.current.volume = newValue / 100;
    if (newValue === 0) {
      setIsMuted(true);
    } else {
      setIsMuted(false);
    }
  };

  const handleMuteToggle = () => {
    if (isMuted) {
      audioRef.current.volume = volume / 100;
    } else {
      audioRef.current.volume = 0;
    }
    setIsMuted(!isMuted);
  };

  const handleProgressChange = (event, newValue) => {
    const newTime = (newValue / 100) * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setProgress(newValue);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", sm: "600px", md: "800px" },
        mx: "auto",
        my: { xs: 2, sm: 3 },
        px: { xs: 1.5, sm: 2 },
        py: 2,
        borderRadius: 2,
        bgcolor: "background.paper",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        },
        transition: "all 0.3s ease-in-out",
        overflow: "hidden",
      }}
    >
      {audioURL ? (
        <>
          <audio
            ref={audioRef}
            src={audioSource}
            onTimeUpdate={(e) => {
              const currentProgress =
                (e.target.currentTime / duration) * 100 || 0;
              setProgress(currentProgress);
              setCurrentTime(e.target.currentTime);
            }}
            onLoadedMetadata={(e) => {
              setDuration(e.target.duration);
            }}
            onEnded={() => setIsPlaying(false)}
            style={{ display: "none" }}
          >
            <track
              kind="captions"
              src=""
              srcLang="en"
              label="English"
              default
            />
          </audio>

          {/* Progress Bar */}
          <Box sx={{ px: 1.5, width: "100%" }}>
            <CustomSlider
              value={progress || 0}
              onChange={handleProgressChange}
              aria-labelledby="audio-progress"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              width: "100%",
              gap: 1.5,
              alignItems: "center",
              flexWrap: "nowrap",
              overflow: "hidden",
            }}
          >
            {/* Left Side: Play Button and Time */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flex: "1 1 auto",
                minWidth: 0,
              }}
            >
              {/* Play/Pause Button */}
              <IconButton
                onClick={handlePlayPause}
                sx={{
                  color: "primary.main",
                  "&:hover": {
                    transform: "scale(1.1)",
                    bgcolor: "transparent",
                  },
                  transition: "transform 0.2s",
                  p: 0.5,
                  flexShrink: 0,
                }}
              >
                {isPlaying ? (
                  <PauseCircle sx={{ fontSize: { xs: 36, sm: 40 } }} />
                ) : (
                  <PlayCircle sx={{ fontSize: { xs: 36, sm: 40 } }} />
                )}
              </IconButton>

              {/* Time Display */}
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  minWidth: 90,
                  textAlign: "left",
                  fontSize: "0.9rem",
                  flexShrink: 0,
                }}
              >
                {formatTime(currentTime)} / {formatTime(duration)}
              </Typography>
            </Box>

            {/* Right Side: Volume Controls */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                width: "auto",
                flexShrink: 0,
              }}
            >
              <IconButton
                onClick={handleMuteToggle}
                size="small"
                sx={{ color: "text.secondary" }}
              >
                {isMuted || volume === 0 ? (
                  <VolumeOff />
                ) : volume < 50 ? (
                  <VolumeDown />
                ) : (
                  <VolumeUp />
                )}
              </IconButton>
              <CustomSlider
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                aria-labelledby="volume-control"
                size="small"
                sx={{
                  ml: { xs: 0.5, sm: 1 },
                  width: "80px",
                  flexShrink: 0,
                }}
              />
            </Box>
          </Box>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Nessun file audio disponibile
        </Typography>
      )}
    </Box>
  );
}

export default CustomAudioPlayer;
